<template>
  <div class="flex flex-col gap-4">
    <!-- Header -->
    <header class="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 class="wh-display text-[22px] font-bold leading-tight tracking-tight text-primary-500 sm:text-[30px]">Devices</h1>
        <p class="mt-0.5 text-[12px] text-gray-500 sm:text-[13px] dark:text-slate-400">
          Redirections MQTT — {{ totalItems }} device{{ totalItems > 1 ? 's' : '' }}
        </p>
      </div>
      <button class="wh-btn wh-btn-primary" @click="navigateToNewDevice">
        <svg viewBox="0 0 24 24" class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M5 12h14" /></svg>
        Ajouter
      </button>
    </header>

    <!-- Toolbar -->
    <div class="flex flex-wrap items-center gap-2 sm:gap-2.5">
      <div class="flex h-9 w-full items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 sm:w-auto sm:min-w-[260px] sm:max-w-[380px] dark:border-slate-600 dark:bg-slate-800">
        <svg viewBox="0 0 24 24" class="h-4 w-4 shrink-0 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
        <input type="text" class="h-full flex-1 border-0 bg-transparent text-[13px] text-gray-900 outline-none placeholder:text-gray-400 focus:ring-0 dark:text-slate-100" placeholder="Rechercher par ID, nom, type, env…" v-model="searchQuery" @input="debounceSearch">
      </div>

      <wh-select class="w-[160px]" :options="typeFilterOptions" v-model="typeFilter" placeholder="Type" @update:modelValue="onFilterChange" />
      <wh-select class="w-[190px]" :options="environmentFilterOptions" v-model="environmentFilter" placeholder="Environnement" @update:modelValue="onFilterChange" />
      <label class="flex items-center gap-1.5 text-[12px] text-gray-600 dark:text-slate-300" title="Affiche les cles de telemetrie client:{id}:connected_env (env de connexion reelle) pour reperer les ecarts avec l'env de provisioning">
        <input type="checkbox" v-model="showConnectedEnv" @change="onFilterChange" />
        connected_env
      </label>
      <button v-if="searchQuery || typeFilter || environmentFilter" class="wh-btn wh-btn-ghost wh-btn-sm" @click="resetFilters">Réinitialiser</button>

      <div class="ml-auto flex items-center gap-2">
        <button class="wh-btn wh-btn-outline wh-btn-sm" @click="openImportModal">
          <svg viewBox="0 0 24 24" class="h-3.5 w-3.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" /></svg>
          Importer CSV
        </button>
        <button class="wh-btn wh-btn-ghost wh-btn-sm" @click="downloadSampleCsv">
          <svg viewBox="0 0 24 24" class="h-3.5 w-3.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
          Exemple
        </button>
      </div>
    </div>

    <!-- States -->
    <div v-if="loading" class="grid place-items-center py-20">
      <span class="h-7 w-7 animate-spin rounded-full border-2 border-gray-200 border-t-primary-500"></span>
    </div>
    <div v-else-if="error" class="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">{{ error }}</div>

    <div v-else-if="devices.length === 0" class="wh-card flex flex-col items-center gap-3 px-4 py-14 text-center">
      <svg viewBox="0 0 24 24" class="h-9 w-9 text-gray-300 dark:text-slate-600" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
      <div>
        <h3 class="text-[15px] font-semibold text-gray-900 dark:text-slate-100">Aucun device trouvé</h3>
        <p class="mt-1 text-[13px] text-gray-500 dark:text-slate-400">Ajuste tes filtres ou ajoute un nouveau device.</p>
      </div>
      <button class="wh-btn wh-btn-primary" @click="navigateToNewDevice">Ajouter un device</button>
    </div>

    <!-- Table -->
    <div v-else class="wh-card overflow-hidden">
      <div class="overflow-x-auto">
        <table class="wh-table">
          <thead>
            <tr>
              <th>Client ID</th><th>Nom</th><th>Type</th><th>Environnement</th><th class="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="device in devices" :key="device.clientId">
              <td><span class="font-mono text-[12.5px] font-semibold text-gray-900 dark:text-slate-100">{{ device.clientId }}</span></td>
              <td>
                <span v-if="device.name" class="text-gray-700 dark:text-slate-200">{{ device.name }}</span>
                <span v-else class="text-gray-400 dark:text-slate-500">—</span>
              </td>
              <td>
                <span class="wh-badge" :style="softStyle(typeColor(device.type))">
                  <span class="wh-dot" :style="{ backgroundColor: typeColor(device.type) }"></span>{{ device.type || 'non défini' }}
                </span>
              </td>
              <td>
                <span class="wh-badge" :style="softStyle(envColor(device.environment))">
                  <span class="wh-dot" :style="{ backgroundColor: envColor(device.environment) }"></span>{{ device.environment }}
                </span>
              </td>
              <td>
                <div class="flex justify-end gap-1">
                  <button class="wh-icon-btn" :class="{ '!text-green-600': syncedId === device.clientId }" :title="syncedId === device.clientId ? 'Synchronisé ✓' : 'Synchroniser (republier env + nom vers les managers)'" :disabled="syncingId === device.clientId" @click="syncDevice(device)">
                    <svg viewBox="0 0 24 24" class="h-4 w-4" :class="{ 'animate-spin': syncingId === device.clientId }" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 2v6h-6M3 22v-6h6M3.5 9a9 9 0 0 1 14.9-3.4L21 8M20.5 15a9 9 0 0 1-14.9 3.4L3 16" /></svg>
                  </button>
                  <button class="wh-icon-btn" title="Modifier" @click="editDevice(device)">
                    <svg viewBox="0 0 24 24" class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
                  </button>
                  <button class="wh-icon-btn hover:!bg-red-50 hover:!text-red-600 dark:hover:!bg-red-500/10" title="Supprimer" @click="confirmDelete(device)">
                    <svg viewBox="0 0 24 24" class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /></svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Footer / pagination -->
      <div class="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-4 py-3 dark:border-slate-700/60">
        <div class="flex items-center gap-3 text-[12px] text-gray-500 dark:text-slate-400">
          <span>{{ devices.length }} / {{ totalItems }} devices</span>
          <span class="flex items-center gap-1.5">
            Afficher
            <select class="wh-select h-8 w-[74px] px-2 pr-7 text-[12px]" style="background-position: right 0.45rem center; background-size: 0.85rem" v-model="itemsPerPage" @change="onPageSizeChange">
              <option value="10">10</option><option value="25">25</option><option value="50">50</option><option value="100">100</option>
            </select>
          </span>
        </div>
        <nav class="flex items-center gap-1">
          <button class="wh-pager" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">‹</button>
          <button v-if="currentPage > 3" class="wh-pager" @click="changePage(1)">1</button>
          <span v-if="currentPage > 3" class="px-1 text-gray-400">…</span>
          <button v-for="page in displayedPages" :key="page" class="wh-pager" :class="page === currentPage ? 'wh-pager-active' : ''" @click="changePage(page)">{{ page }}</button>
          <span v-if="currentPage < totalPages - 2" class="px-1 text-gray-400">…</span>
          <button v-if="currentPage < totalPages - 2 && totalPages > 1" class="wh-pager" @click="changePage(totalPages)">{{ totalPages }}</button>
          <button class="wh-pager" :disabled="currentPage === totalPages || totalPages === 0" @click="changePage(currentPage + 1)">›</button>
        </nav>
      </div>
    </div>

    <!-- Modal suppression -->
    <div v-if="showDeleteModal" class="wh-backdrop" @click.self="showDeleteModal = false">
      <div class="wh-modal">
        <div class="flex items-center justify-between border-b border-gray-100 px-5 py-3.5 dark:border-slate-700">
          <h3 class="text-[15px] font-semibold text-gray-900 dark:text-slate-100">Supprimer ce device&nbsp;?</h3>
          <button class="wh-icon-btn" @click="showDeleteModal = false">✕</button>
        </div>
        <div v-if="deviceToDelete" class="px-5 py-4">
          <div class="rounded-lg bg-gray-50 p-3 text-[13px] dark:bg-slate-900/50">
            <div class="font-mono font-semibold text-gray-900 dark:text-slate-100">{{ deviceToDelete.clientId }}</div>
            <div v-if="deviceToDelete.name" class="mt-0.5 text-gray-500 dark:text-slate-400">{{ deviceToDelete.name }}</div>
            <div class="mt-2 flex gap-1.5">
              <span class="wh-badge" :style="softStyle(typeColor(deviceToDelete.type))"><span class="wh-dot" :style="{ backgroundColor: typeColor(deviceToDelete.type) }"></span>{{ deviceToDelete.type || 'non défini' }}</span>
              <span class="wh-badge" :style="softStyle(envColor(deviceToDelete.environment))"><span class="wh-dot" :style="{ backgroundColor: envColor(deviceToDelete.environment) }"></span>{{ deviceToDelete.environment }}</span>
            </div>
          </div>
          <p class="mt-3 text-[12px] text-red-600 dark:text-red-400">Cette action est irréversible.</p>
        </div>
        <div class="flex justify-end gap-2 border-t border-gray-100 px-5 py-3.5 dark:border-slate-700">
          <button class="wh-btn wh-btn-outline" @click="showDeleteModal = false">Annuler</button>
          <button class="wh-btn wh-btn-danger" @click="deleteDevice">Supprimer</button>
        </div>
      </div>
    </div>

    <!-- Modal import CSV -->
    <div v-if="showImportModal" class="wh-backdrop" @click.self="importStatus === 'idle' && (showImportModal = false)">
      <div class="wh-modal">
        <div class="flex items-center justify-between border-b border-gray-100 px-5 py-3.5 dark:border-slate-700">
          <h3 class="text-[15px] font-semibold text-gray-900 dark:text-slate-100">Importer des devices (CSV)</h3>
          <button class="wh-icon-btn" @click="showImportModal = false">✕</button>
        </div>
        <div class="px-5 py-4">
          <template v-if="importStatus === 'idle'">
            <p class="text-[13px] text-gray-600 dark:text-slate-300">Sélectionne un CSV avec les colonnes
              <code class="rounded bg-gray-100 px-1 text-[12px] dark:bg-slate-900/60">clientId</code>,
              <code class="rounded bg-gray-100 px-1 text-[12px] dark:bg-slate-900/60">name</code>,
              <code class="rounded bg-gray-100 px-1 text-[12px] dark:bg-slate-900/60">type</code>,
              <code class="rounded bg-gray-100 px-1 text-[12px] dark:bg-slate-900/60">environment</code>.
            </p>
            <input type="file" class="wh-input mt-3 h-auto py-2 file:mr-3 file:rounded-md file:border-0 file:bg-primary-50 file:px-3 file:py-1.5 file:text-[12px] file:font-semibold file:text-primary-600" ref="fileInput" accept=".csv" @change="handleFileChange">
          </template>
          <div v-else-if="importStatus === 'uploading'" class="grid place-items-center gap-3 py-6">
            <span class="h-7 w-7 animate-spin rounded-full border-2 border-gray-200 border-t-primary-500"></span>
            <p class="text-[13px] text-gray-500 dark:text-slate-400">Import en cours…</p>
          </div>
          <div v-else-if="importStatus === 'success'" class="py-2 text-center">
            <div class="mx-auto grid h-12 w-12 place-items-center rounded-full bg-emerald-50 text-emerald-500 dark:bg-emerald-500/10">
              <svg viewBox="0 0 24 24" class="h-6 w-6" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
            </div>
            <h4 class="mt-3 text-[15px] font-semibold text-gray-900 dark:text-slate-100">Import terminé</h4>
            <p class="mt-2 text-[13px] text-gray-700 dark:text-slate-200"><strong>{{ importResults.imported }}</strong> devices importés</p>
            <p v-if="importResults.duplicates > 0" class="text-[13px] text-warmYellow-600">{{ importResults.duplicates }} ignorés (déjà existants)</p>
            <p v-if="importResults.errors > 0" class="text-[13px] text-red-600">{{ importResults.errors }} erreurs</p>
          </div>
          <div v-else-if="importStatus === 'error'" class="py-2 text-center">
            <div class="mx-auto grid h-12 w-12 place-items-center rounded-full bg-red-50 text-red-500 dark:bg-red-500/10">
              <svg viewBox="0 0 24 24" class="h-6 w-6" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
            </div>
            <h4 class="mt-3 text-[15px] font-semibold text-gray-900 dark:text-slate-100">Erreur lors de l'import</h4>
            <p class="mt-1 text-[13px] text-red-600 dark:text-red-400">{{ importError }}</p>
          </div>
        </div>
        <div class="flex justify-end gap-2 border-t border-gray-100 px-5 py-3.5 dark:border-slate-700">
          <template v-if="importStatus === 'idle'">
            <button class="wh-btn wh-btn-outline" @click="showImportModal = false">Annuler</button>
            <button class="wh-btn wh-btn-primary" :disabled="!selectedFile" @click="uploadCsv">Importer</button>
          </template>
          <button v-else-if="importStatus === 'success' || importStatus === 'error'" class="wh-btn wh-btn-primary" @click="closeImportModal">Fermer</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../api'
import WhSelect from '../components/WhSelect.vue'
import { DEVICE_TYPES, ENVIRONMENTS, envColor, typeColor, softStyle } from '../constants'

export default {
  name: 'DeviceList',
  components: { WhSelect },
  data() {
    return {
      searchTimeout: null,
      devices: [],
      loading: true,
      error: null,
      searchQuery: '',
      typeFilter: '',
      environmentFilter: '',
      showConnectedEnv: false,
      syncingId: null,
      syncedId: null,
      deviceToDelete: null,
      showDeleteModal: false,
      showImportModal: false,
      selectedFile: null,
      importStatus: 'idle',
      importResults: { imported: 0, duplicates: 0, errors: 0 },
      importError: '',
      deviceTypes: DEVICE_TYPES,
      environments: ENVIRONMENTS,
      currentPage: 1,
      itemsPerPage: 10,
      totalItems: 0,
      totalPages: 0,
    }
  },
  computed: {
    typeFilterOptions() {
      return [{ value: '', label: 'Tous les types' }, ...this.deviceTypes]
    },
    environmentFilterOptions() {
      return [{ value: '', label: 'Tous les environnements' }, ...this.environments]
    },
    displayedPages() {
      if (this.totalPages === 0) return []
      let start = Math.max(1, this.currentPage - 1)
      let end = Math.min(this.totalPages, start + 2)
      if (end - start < 2) start = Math.max(1, end - 2)
      const pages = []
      for (let i = start; i <= end; i++) pages.push(i)
      return pages
    },
  },
  methods: {
    envColor, typeColor, softStyle,
    async fetchDevices() {
      this.loading = true
      this.error = null
      try {
        const params = { page: this.currentPage, limit: this.itemsPerPage }
        if (this.searchQuery) params.search = this.searchQuery
        if (this.typeFilter) params.type = this.typeFilter
        if (this.environmentFilter) params.environment = this.environmentFilter
        if (this.showConnectedEnv) params.includeConnectedEnv = 'true'
        const { data } = await api.get('/devices', { params })
        this.devices = data.devices
        this.totalItems = data.pagination.totalItems
        this.totalPages = data.pagination.totalPages
        this.currentPage = data.pagination.page
      } catch (err) {
        console.error('Erreur lors du chargement des devices:', err)
        this.error = 'Impossible de charger les devices. Veuillez réessayer plus tard.'
      } finally {
        this.loading = false
      }
    },
    confirmDelete(device) { this.deviceToDelete = device; this.showDeleteModal = true },
    async deleteDevice() {
      if (!this.deviceToDelete) return
      try {
        await api.delete(`/devices/${this.deviceToDelete.clientId}`)
        this.devices = this.devices.filter(d => d.clientId !== this.deviceToDelete.clientId)
        this.showDeleteModal = false
        this.deviceToDelete = null
      } catch (err) {
        console.error('Erreur lors de la suppression:', err)
        alert('Impossible de supprimer le device. Veuillez réessayer plus tard.')
      }
    },
    async syncDevice(device) {
      this.syncingId = device.clientId
      try {
        await api.post(`/devices/${device.clientId}/sync`)
        this.syncedId = device.clientId
        setTimeout(() => { if (this.syncedId === device.clientId) this.syncedId = null }, 2000)
      } catch (err) {
        console.error('Erreur synchro provisioning:', err)
        alert('Échec de la synchronisation vers les managers. Veuillez réessayer plus tard.')
      } finally {
        this.syncingId = null
      }
    },
    editDevice(device) { this.$router.push(`/device/${device.clientId}`) },
    navigateToNewDevice() { this.$router.push('/device/new') },
    changePage(page) {
      if (page < 1 || page > this.totalPages) return
      this.currentPage = page
      this.fetchDevices()
    },
    debounceSearch() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => { this.currentPage = 1; this.fetchDevices() }, 300)
    },
    onFilterChange() { this.currentPage = 1; this.fetchDevices() },
    onPageSizeChange() { this.currentPage = 1; this.fetchDevices() },
    resetFilters() {
      this.searchQuery = ''; this.typeFilter = ''; this.environmentFilter = ''
      this.currentPage = 1; this.fetchDevices()
    },
    openImportModal() {
      this.importStatus = 'idle'
      this.selectedFile = null
      this.importError = ''
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
      this.showImportModal = true
    },
    handleFileChange(event) { this.selectedFile = event.target.files[0] || null },
    async uploadCsv() {
      if (!this.selectedFile) return
      const formData = new FormData()
      formData.append('file', this.selectedFile)
      this.importStatus = 'uploading'
      try {
        const { data } = await api.post('/devices/import-csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
        this.importResults = { imported: data.imported, duplicates: data.duplicates, errors: data.errors }
        this.importStatus = 'success'
        if (data.imported > 0) await this.fetchDevices()
      } catch (err) {
        console.error("Erreur lors de l'import CSV:", err)
        this.importError = err.response?.data?.error || "Erreur lors de l'import du fichier"
        this.importStatus = 'error'
      }
    },
    closeImportModal() { this.showImportModal = false },
    async downloadSampleCsv() {
      try {
        const { data } = await api.get('/devices/sample-csv', { responseType: 'blob' })
        const url = window.URL.createObjectURL(new Blob([data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'exemple_import.csv')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (err) {
        console.error("Erreur lors du téléchargement de l'exemple CSV:", err)
        alert("Impossible de télécharger l'exemple CSV. Veuillez réessayer plus tard.")
      }
    },
  },
  mounted() { this.fetchDevices() },
}
</script>

<style scoped>
.wh-pager {
  @apply inline-flex h-8 min-w-8 items-center justify-center rounded-md px-2 text-[13px] font-medium text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 dark:text-slate-300 dark:hover:bg-slate-700;
}
.wh-pager-active {
  @apply bg-primary-500 text-white hover:bg-primary-600 dark:hover:bg-primary-600;
}
</style>
